import { Domain, User, UserRole } from "@lr-notitia/notitia-designer/schemas";
import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { api } from "@lr-notitia/notitia-designer/client";
import { LanguageCode } from "iso-639-1";

import { useTemplateProcessesStore } from "./t-process";
import { useTemplateWorkspacesStore } from "./t-workspace";

let templateProcessesStore: ReturnType<typeof useTemplateProcessesStore>;
let templateWorkspacesStore: ReturnType<typeof useTemplateWorkspacesStore>;
function init() {
	templateProcessesStore = useTemplateProcessesStore();
	templateWorkspacesStore = useTemplateWorkspacesStore();
}

export const useDomainStore = defineStore("domain", () => {
	let domainId = "";
	const notFound = ref(true);
	const domain = ref<Domain>({} as Domain);
	const account = ref<User>({} as User);
	const members = ref<User[]>([]);
	const languages = ref<LanguageCode[]>([]);
	const languagePreference = ref<LanguageCode>(
		getLocalStorageLanguage() && getLocalStorageLanguage() !== ""
			? (getLocalStorageLanguage() as LanguageCode)
			: ((navigator.language.split("-")[0] || "en") as LanguageCode)
	);
	const allDomains = ref<Domain[]>([]);

	const isAdmin = computed(() => account.value.role === UserRole.Admin);
	const isEditorOrAdmin = computed(() => account.value.role === UserRole.Editor || isAdmin.value);

	function loadDomain(dId: string): Promise<Domain> {
		domainId = dId;
		return new Promise((resolve) => {
			api()
				.domain(domainId)
				.get()
				.then((data) => {
					domain.value = data;
					languages.value = data.languages;
					notFound.value = false;
					loadAccount(dId)
						.then(() => loadMembers(dId))
						.then(() => resolve(data));
				})
				.catch(() => console.warn("Failed to load domain"));
		});
	}

	function clearDomain() {
		notFound.value = true;
		domain.value = {} as Domain;
		account.value = {} as User;
		members.value = [];
		domainId = "";
	}

	function loadAllDomains(): Promise<Domain[]> {
		return new Promise((resolve) => {
			api()
				.domains()
				.get()
				.then((data) => {
					allDomains.value = data;
					resolve(data);
				});
		});
	}

	function loadAccount(dId: string): Promise<void> {
		return new Promise((resolve) => {
			api()
				.domain(dId)
				.account()
				.get()
				.then((data) => {
					account.value = data;
					resolve();
				})
				.catch(() => console.warn("Failed to load Account"));
		});
	}

	function loadMembers(dId: string) {
		api()
			.domain(dId)
			.members()
			.get()
			.then((data) => (members.value = data))
			.catch(() => console.warn("Failed to load Members"));
	}

	function searchMember(query: string): Promise<User[]> {
		return new Promise((resolve) => {
			api()
				.domain(domainId)
				.searchUsers()
				.get(query)
				.then((data) => resolve(data))
				.catch(() => resolve([]));
		});
	}

	function addMember(user: User, role: UserRole) {
		api()
			.domain(domainId)
			.members()
			.post({ userId: user.id, role })
			.then((d) => (members.value = d))
			.catch(() => console.warn("Failed to add member"));
	}

	function moveMember(user: User, role: UserRole) {
		if (!canModify(role)) return;
		api()
			.domain(domainId)
			.members()
			.del({ userId: user.id })
			.then(() => {
				api()
					.domain(domainId)
					.members()
					.post({ userId: user.id, role })
					.then((d) => (members.value = d))
					.catch(() => console.warn("Failed to move member"));
			})
			.catch(() => console.warn("Failed to move member"));
		return;
	}

	function removeMember(userId: string) {
		api()
			.domain(domainId)
			.members()
			.del({ userId })
			.then(() => {
				members.value = members.value.filter((m) => m.id !== userId);
			})
			.catch(() => console.warn("Failed to remove member"));
	}

	function canModify(targetUserRole: UserRole) {
		let modifyableRoles: string[] = [];
		switch (account.value.role) {
			case UserRole.Admin:
				modifyableRoles = [UserRole.Admin, UserRole.Editor, UserRole.Viewer];
				break;
			case UserRole.Editor:
				modifyableRoles = [UserRole.Viewer];
				break;
			case UserRole.Viewer:
				modifyableRoles = [];
				break;
		}
		if (modifyableRoles.includes(targetUserRole)) return true;
		else return false;
	}

	function hasAccessRights(neededRole: UserRole): boolean {
		if (neededRole === UserRole.Admin) return isAdmin.value;
		else if (neededRole === UserRole.Editor) return isEditorOrAdmin.value;
		else if (neededRole === UserRole.Viewer) return true;
		return false;
	}

	function getLocalStorageLanguage(): string {
		return localStorage.getItem("languagePreference") || "";
	}
	function setLocalStorageLanguage(lang: LanguageCode) {
		localStorage.setItem("languagePreference", lang);
	}

	function domainColor(domainId: string): string {
		if (!domainId) return "#ffffff";
		const colors = [
			"#b2dfdb",
			"#f0f4c3",
			"#c8e6c9",
			"#ffcdd2",
			"#f8bbd0",
			"#ffe0b2",
			"#b3e5fc",
			"#d1c4e9",
			"#4db6ac",
			"#dce775",
			"#81c784",
			"#e57373",
			"#f06292",
			"#ffb74d",
			"#4fc3f7",
			"#9575cd",
			"#00796b",
			"#afb42b",
			"#388e3c",
			"#d32f2f",
			"#c2185b",
			"#f57c00",
			"#1486c9",
			"#4c3b8f",
		];
		const hash = domainId.split("").reduce((a, b) => {
			a = (a << 5) - a + b.charCodeAt(0);
			return a & a;
		}, 0);
		return colors[Math.abs(hash) % colors.length];
	}

	watch(
		() => members.value,
		() => {
			members.value.sort((a, b) => {
				if (a.lastname < b.lastname) return -1;
				if (a.lastname > b.lastname) return 1;
				return 0;
			});
		}
	);
	watch(
		() => allDomains.value,
		() => {
			allDomains.value.sort((a, b) => {
				if (a.name < b.name) return -1;
				if (a.name > b.name) return 1;
				return 0;
			});
		}
	);
	watch(
		() => languagePreference.value,
		(newVal) => {
			setLocalStorageLanguage(newVal);
			if (!templateProcessesStore || !templateWorkspacesStore) init();
			templateProcessesStore.sortAlphabetically(templateProcessesStore.processes);
			templateWorkspacesStore.sortAlphabetically(templateWorkspacesStore.workspaces);
		}
	);

	return {
		notFound,
		domain,
		account,
		members,
		isAdmin,
		isEditorOrAdmin,
		loadDomain,
		clearDomain,
		loadAccount,
		loadMembers,
		searchMember,
		addMember,
		moveMember,
		removeMember,
		canModify,
		languages,
		languagePreference,
		hasAccessRights,
		domainColor,
		allDomains,
		loadAllDomains,
	};
});
